import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { Lock, Mail, ArrowRight, Building2, CheckCircle, Users, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import LanguageSelector from '../components/LanguageSelector';
import AuthLayout from '../components/AuthLayout';

const _BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(email, password, 'member');

    if (result.success) {
      toast.success('Connexion réussie !');
      const role = result.user?.role;
      const back = sessionStorage.getItem('redirect_after_login');
      sessionStorage.removeItem('redirect_after_login');
      if (back && back.startsWith('/') && !back.startsWith('//')
        && ((back.startsWith('/admin') && ['admin', 'super_admin'].includes(role)) || (back.startsWith('/member') && role === 'member'))) navigate(back);
      else if (role === 'cooper') navigate('/cooper');
      else if (role === 'expert') navigate('/expert');
      else if (role === 'expert_comptable') navigate('/expert-comptable');
      else if (role === 'member' || role === 'beneficiaire_pro') navigate('/member');
      else if (role === 'beneficiaire_particulier') navigate('/beneficiaire-particulier');
      else navigate('/');
    } else {
      // Si l'utilisateur est un admin → on l'oriente vers la bonne console
      const adminHint =
        result.error?.includes('administrateurs') ||
        result.error?.toLowerCase().includes('super admin');
      if (adminHint) {
        toast.error(result.error, {
          description: 'Redirection vers la console Super Admin…',
          duration: 4000,
        });
        setTimeout(() => navigate('/admin/login'), 1500);
      } else {
        toast.error(result.error);
      }
    }

    setLoading(false);
  };

  return (
    <AuthLayout
      variant="default"
      badge="Espace membres"
      badgeIcon={Users}
      title="Bienvenue sur"
      titleHighlight="O'SCOP Outremer"
      description="Connectez-vous pour accéder à votre espace dédié : Membre, COOPER, Expert, Partenaire, Bénéficiaire. La plateforme officielle au service de l'économie sociale ultramarine."
      features={[
        { icon: Building2, label: 'Accès aux dispositifs de l\'écosystème O\'SCOP' },
        { icon: Users, label: 'Réseau des COOPER, experts et partenaires' },
        { icon: CheckCircle, label: 'Espace dédié aux bénéficiaires' },
      ]}
    >
      {/* Top right: language selector */}
      <div className="flex justify-end mb-4">
        <LanguageSelector variant="compact" />
      </div>

      <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2">
        Connexion à votre espace
      </h2>
      <p className="text-slate-600 dark:text-slate-300 text-sm mb-7">
        Entrez vos identifiants pour accéder à votre tableau de bord personnel.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
        <div>
          <Label htmlFor="email" className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            {t('login.email', 'Adresse email')}
          </Label>
          <div className="relative mt-1.5">
            <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vous@exemple.com"
              className="pl-10 h-12"
              required
              autoComplete="email"
              data-testid="login-email-input"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="password" className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            {t('login.password', 'Mot de passe')}
          </Label>
          <div className="relative mt-1.5">
            <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
            <PasswordInput
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="pl-10 h-12"
              required
              autoComplete="current-password"
              data-testid="login-password-input"
            />
          </div>
        </div>

        <div className="flex items-center justify-end text-sm">
          <Link
            to="/forgot-password"
            className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
            data-testid="forgot-password-link"
          >
            Mot de passe oublié ?
          </Link>
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="w-full h-12 text-base font-semibold bg-gradient-to-r from-[#0B3C5D] to-[#0a1e3f] hover:from-[#0a3252] hover:to-[#091a36] shadow-lg shadow-blue-900/30 hover:shadow-xl group"
          data-testid="login-submit-btn"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          ) : (
            <ArrowRight className="w-5 h-5 mr-2 group-hover:translate-x-0.5 transition-transform" />
          )}
          {loading ? 'Connexion...' : 'Se connecter'}
        </Button>
      </form>

      <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700 space-y-3 text-center">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Pas encore de compte ?{' '}
          <Link to="/register" className="text-blue-600 dark:text-blue-400 font-semibold hover:underline">
            Créer un compte
          </Link>
        </p>
        <p className="text-xs text-slate-400 dark:text-slate-500">
          Vous êtes administrateur ?{' '}
          <Link to="/admin/login" className="text-slate-600 dark:text-slate-300 hover:underline">
            Connexion admin
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}
